import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import { tokenStore } from '@lib/tokenStore';
import { prefsStore } from '@lib/prefsStore';
import { userAuthenticated, userSignedOut } from './slice';
import { normalizeLoginResponse } from './tokenResponse';
import type { InvitationValidation, LoginResponse, OnboardRequest, PasswordPolicy } from './types';
import { onboardRequestBuilders } from './onboardRequestBuilders';

interface LoginArgs {
  username: string;
  password: string;
}

interface ServerError {
  errorMessage?: string;
  ErrorMessage?: string;
}

interface ErrorPayload {
  errors?: ServerError[];
  Errors?: ServerError[];
}

function extractErrorMessage(data: unknown, fallback: string): string {
  if (!data || typeof data !== 'object') return fallback;
  const payload = data as ErrorPayload;
  const arr = payload.errors ?? payload.Errors;
  const first = arr?.[0];
  return first?.errorMessage ?? first?.ErrorMessage ?? fallback;
}

/**
 * Raw `GET /v1/password-policy` body. The user-service `PasswordPolicyDTO` is
 * PascalCase in C#, but the JSON serializer's casing isn't pinned in source
 * (swagger examples show camelCase). Rather than guess, we accept BOTH casings
 * and normalize in `transformResponse` — the same defensive tolerance the
 * login/refresh paths apply to `tokenResponse` and `errors`/`Errors`.
 */
interface PasswordPolicyWire {
  length?: number;
  Length?: number;
  maxLength?: number;
  MaxLength?: number;
  lowerCase?: number;
  LowerCase?: number;
  upperCase?: number;
  UpperCase?: number;
  digits?: number;
  Digits?: number;
  specialChars?: number;
  SpecialChars?: number;
}

function normalizePasswordPolicy(raw: PasswordPolicyWire): PasswordPolicy {
  return {
    length: raw.length ?? raw.Length ?? 0,
    maxLength: raw.maxLength ?? raw.MaxLength ?? 0,
    lowerCase: raw.lowerCase ?? raw.LowerCase ?? 0,
    upperCase: raw.upperCase ?? raw.UpperCase ?? 0,
    digits: raw.digits ?? raw.Digits ?? 0,
    specialChars: raw.specialChars ?? raw.SpecialChars ?? 0,
  };
}

/**
 * Query builders for the password-reset endpoints, exported so the exact URL +
 * method + body can be wire-shape-locked in api.test.ts (the repo convention —
 * see shipments/pricing `*RequestBuilders`). All three verified against the
 * user-service C# controllers, the live OpenAPI spec, and legacy
 * `userServices.js`; the password-policy GET was also pinned live (200,
 * camelCase body).
 */
export const authRequestBuilders = {
  /**
   * Request a reset email. NO query params. Was previously pointed at the
   * non-existent `/v1/password/reset-request` (404, so no email
   * ever sent) — corrected to the real `/v1/forgot-password`.
   */
  requestPasswordReset: ({ email, captchaToken }: { email: string; captchaToken?: string }) => ({
    url: `${services.user}/v1/forgot-password`,
    method: 'POST' as const,
    body: { email },
    ...(captchaToken ? { headers: { 'X-Captcha-Token': captchaToken } } : {}),
  }),
  /** Public (`[AllowAnonymous]`) password policy for the requirements checklist. */
  getPasswordPolicy: () => ({
    url: `${services.user}/v1/password-policy`,
    method: 'GET' as const,
  }),
  /**
   * Complete the reset with the email-link token. Token is interpolated raw
   * (not encodeURIComponent'd) to match legacy and avoid corrupting a
   * base64url token's bytes — it arrives as an already-URL-safe path segment.
   * NO query params; body carries only the new password.
   */
  confirmPasswordReset: ({ token, password }: { token: string; password: string }) => ({
    url: `${services.user}/v1/reset-password/${token}`,
    method: 'POST' as const,
    body: { password },
  }),
};

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: portalBaseQuery,
  endpoints: (builder) => ({
    /**
 * Sign-in.
 * On success: store tokens in TokenStore and populate the auth slice.
 * Skips the 401-retry middleware (an invalid login is the user's fault,
 * not an expired session).
 */
    login: builder.mutation<LoginResponse, LoginArgs>({
      query: (body) => ({
        url: `${services.user}/v1/login`,
        method: 'POST',
        body,
      }),
      extraOptions: { skipAuthRetry: true },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Variant tolerance: server may ship tokens flat OR nested under
          // `tokenResponse`. reducer handled both shapes; ported
          // here so a backend revert doesn't 500-out the client login flow.
          const normalized = normalizeLoginResponse(data);
          if (!normalized) return;
          tokenStore.setAccessToken(normalized.accessToken, normalized.expiresIn);
          tokenStore.setRefreshToken(normalized.refreshToken);
          // A fresh sign-in starts with clean list filters — the persisted
          // orders/shipments query strings must not leak across sessions or
          // users. Session rehydrate (AuthBootstrap) keeps them.
          prefsStore.clearSearches();
          // Strip token fields before stashing the user in Redux — secrets
          // never live in the store, only in TokenStore.
          const { accessToken: _a, refreshToken: _r, expiresIn: _e, tokenType: _t, scope: _s,...user } = normalized;
          dispatch(userAuthenticated(user));
        } catch {
          // The component handles the error via the mutation result; nothing to do here.
        }
      },
      transformErrorResponse: (response) => ({
...response,
        message: extractErrorMessage(response.data, 'Sign-in failed'),
      }),
    }),

    /**
 * Sign-out. The user-service exposes no logout endpoint (stateless JWT +
 * refresh-token auth — verified against User API v1's swagger), so signing
 * out is purely client-side: drop the tokens and clear the auth slice. The
 * previous `POST /v1/logout` here returned 405 Method Not Allowed because
 * that route doesn't exist. `queryFn` keeps this a real RTK Query mutation
 * (so `useLogoutMutation` and its call sites are unchanged) while issuing
 * no network request.
 */
    logout: builder.mutation<void, void>({
      queryFn: () => ({ data: undefined }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } finally {
          tokenStore.clearAll();
          dispatch(userSignedOut());
        }
      },
    }),

    /**
 * OAuth code exchange. Used by the OAuth callback page after the user
 * comes back from auth.gooten.com.
 */
    exchangeCode: builder.mutation<LoginResponse, { code: string }>({
      query: (body) => ({
        url: `${services.user}/v1/code`,
        method: 'POST',
        body,
      }),
      extraOptions: { skipAuthRetry: true },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const normalized = normalizeLoginResponse(data);
          if (!normalized) return;
          tokenStore.setAccessToken(normalized.accessToken, normalized.expiresIn);
          tokenStore.setRefreshToken(normalized.refreshToken);
          // Same fresh-session reset as the password login.
          prefsStore.clearSearches();
          const { accessToken: _a, refreshToken: _r, expiresIn: _e, tokenType: _t, scope: _s,...user } = normalized;
          dispatch(userAuthenticated(user));
        } catch {
          // surfaced to the callback page via the mutation result
        }
      },
    }),

    /** Forgot-password trigger: server emails a reset link. See `authRequestBuilders`. */
    requestPasswordReset: builder.mutation<void, { email: string; captchaToken?: string }>({
      query: authRequestBuilders.requestPasswordReset,
      extraOptions: { skipAuthRetry: true },
    }),

    /**
     * Public (`[AllowAnonymous]`) password policy for the requirements
     * checklist — works for an unauthenticated user from a reset link. The
     * `transformResponse` normalizes Pascal/camel casing. See `authRequestBuilders`.
     */
    getPasswordPolicy: builder.query<PasswordPolicy, void>({
      query: authRequestBuilders.getPasswordPolicy,
      extraOptions: { skipAuthRetry: true },
      transformResponse: normalizePasswordPolicy,
    }),

    /**
     * Complete a reset with the email-link token. Public (`[AllowAnonymous]`);
     * the token IS the authorization. 204 success · 404 invalid/expired token ·
     * 400 ValidationError. See `authRequestBuilders`.
     */
    confirmPasswordReset: builder.mutation<void, { token: string; password: string }>({
      query: authRequestBuilders.confirmPasswordReset,
      extraOptions: { skipAuthRetry: true },
    }),

    /**
 * Validate an invitation code (accept-invite / onboard page).
 * Live: GET https://api.dev.ordermesh.io/user/v1/invitation/{code}
 *       GET https://api.dev.ordermesh.io/user/v1/vendor-invitation/{code}
 * Public + skipAuthRetry (the invitee has no session yet). An invalid/expired
 * code returns a 4xx ({ errors: [...] }) → surfaces as `isError`; a valid code
 * returns the InvitationValidation envelope (passed through verbatim).
 */
    validateInvitation: builder.query<InvitationValidation, { invitationCode: string; isVendor: boolean }>({
      query: (args) => onboardRequestBuilders.validateInvitation(args),
      extraOptions: { skipAuthRetry: true },
      transformResponse: (response: InvitationValidation) => response,
    }),

    /**
 * Accept an invitation (create/associate the user).
 * Live: POST https://api.dev.ordermesh.io/user/v1/invitation/onboard
 *       POST https://api.dev.ordermesh.io/user/v1/vendor-invitation/onboard
 * skipAuthRetry (public). The caller signs the user in afterward via `login`.
 */
    onboardUser: builder.mutation<{ id?: string }, { data: OnboardRequest; isVendor: boolean }>({
      query: (args) => onboardRequestBuilders.onboard(args),
      extraOptions: { skipAuthRetry: true },
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useExchangeCodeMutation,
  useRequestPasswordResetMutation,
  useValidateInvitationQuery,
  useGetPasswordPolicyQuery,
  useOnboardUserMutation,
  useConfirmPasswordResetMutation,
} = authApi;
